import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, OnDestroy, input, output, inject, SimpleChanges, OnChanges } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject, take, takeUntil, catchError, of, finalize, combineLatest } from 'rxjs';

// PrimeNG Imports
import { StyleClassModule } from 'primeng/styleclass';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';

// Fitness Force Imports
import { DataGridComponent, EmptyStateContainerComponent } from '@fitness-force/grid';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import type { ClientSubscriptionFreezeResponse, TENANTSINTERFACE, Client, GetClientSubscriptionResponse } from '@fitness-force/models';
import { FreezeService, TimeconversionService, StaffService, ToastMessageService, ClientService } from '@fitness-force/api';
import { ClientSubscriptionFreezeStatus, PlanCategory } from '@fitness-force/enum';

// Local Components
import { FreezeEditComponent } from '../freeze-edit/freeze-edit.component';
import { FreezeCancelComponent } from '../freeze-cancel/freeze-cancel.component';
import { FreezeStopComponent } from '../freeze-stop/freeze-stop.component';
import { EnhancedFreezeSidebarComponent } from '../enhanced-freeze-sidebar/enhanced-freeze-sidebar.component';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { FreezeStatusComponent, StaffInfoComponent } from '@fitness-force/shared';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { TableModule } from 'primeng/table';

@Component({
  selector: 'lib-freeze-list-tab',
  templateUrl: './freeze-list-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    StyleClassModule,
    SkeletonModule,
    ButtonModule,
    RippleModule,
    FreezeEditComponent,
    FreezeCancelComponent,
    FreezeStopComponent,
    EnhancedFreezeSidebarComponent,
    TranslateModule,
    TableModule,
    DataGridComponent,
    AsyncPipe,
    DecimalPipe,
    NgClass
],
})
export class FreezeListTabComponent implements OnInit, OnChanges, OnDestroy {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly freezeService = inject(FreezeService);
  private readonly timeService = inject(TimeconversionService);
  private readonly staffService = inject(StaffService);
  private readonly clientService = inject(ClientService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly cd = inject(ChangeDetectorRef);

  readonly clientId = input(0);
  readonly client = input<Client>(); // Optional - will fetch if not provided
  readonly tenantId = input(0);
  readonly subscriptionId = input(0); // When provided, fetches freeze list by subscription ID
  localTenantId: number = 0;
  localClient$ = new BehaviorSubject<Client | null>(null);
  readonly showTableActions = input(true);
  readonly showHeader = input(true);
  readonly activeSubTab = input<string>();

  readonly isUpdated = output<boolean>();

  freezeData$ = new BehaviorSubject<ClientSubscriptionFreezeResponse[]>([]);
  freezeTableColumns$ = new BehaviorSubject<any>([]);
  selectAllFreezes = false;

  // Feature flag for FreezeV2
  freezeV2Flow = true;

  // Old flow - Edit freeze properties (using freeze-edit component)
  editFreeze$ = new BehaviorSubject<boolean>(false);
  selectedEditFreeze$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);

  // New flow (V2) - Reschedule freeze properties (using enhanced-freeze-sidebar)
  rescheduleFreeze$ = new BehaviorSubject<boolean>(false);
  selectedRescheduleFreeze$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);
  rescheduleSubscription$ = new BehaviorSubject<GetClientSubscriptionResponse>({} as GetClientSubscriptionResponse);

  cancelFreeze$ = new BehaviorSubject<boolean>(false);
  selectedCancelFreeze$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);

  stopFreeze$ = new BehaviorSubject<boolean>(false);
  selectedStopFreeze$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);

  // New flow (V2) - Stop freeze with enhanced sidebar
  stopFreezeV2$ = new BehaviorSubject<boolean>(false);
  selectedStopFreezeV2$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);
  stopSubscription$ = new BehaviorSubject<GetClientSubscriptionResponse>({} as GetClientSubscriptionResponse);

  // New flow (V2) - Cancel freeze with enhanced sidebar
  cancelFreezeV2$ = new BehaviorSubject<boolean>(false);
  selectedCancelFreezeV2$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);
  cancelSubscription$ = new BehaviorSubject<GetClientSubscriptionResponse>({} as GetClientSubscriptionResponse);

  loading = false;
  emptyStateComponent$ = new BehaviorSubject<any>(EmptyStateContainerComponent);
  showBulkActionBar = false;

  // Staff cache to prevent repeated API calls
  private readonly staffCache: Map<number, any> = new Map();
  private readonly destroy$ = new Subject<void>();

  showGridHeader : boolean = false;

  ngOnInit(): void {
    this.showGridHeader = this.showHeader();
    // Auto-expand freeze accordion if activeSubTab is 'freeze'
    if (this.activeSubTab() === 'freeze') {
      this.showGridHeader = false;
    }
    // Resolve the feature flag before initialising so getFreezeList() receives
    // the correct value. getFlag$() waits for Statsig init — no race condition.
    this.featureFlagService.getFlag$('show_freeze_v2')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(isEnabled => {
        this.freezeV2Flow = isEnabled;
        this.componentInit();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to activeSubTab
    if (changes['activeSubTab'] && !changes['activeSubTab'].firstChange) {
      if (this.activeSubTab() === 'freeze') {
        this.showGridHeader = false;
        this.cd.detectChanges();
      }
    }

    if (
      (changes['subscriptionId'] && !changes['subscriptionId'].firstChange) ||
      (changes['showHeader'] && !changes['showHeader'].firstChange)
    ) {
      this.getFreezeTableColumns();
    }

    if (changes['subscriptionId'] && !changes['subscriptionId'].firstChange) {
      this.getFreezeList();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.staffCache.clear();
  }

  componentInit() {
    combineLatest([
      this.store.select(COMPANY_INFO),
      this.store.select(TENANTS_INFO),
    ]).pipe(takeUntil(this.destroy$)).subscribe(([companyInfo, tenant]: [any, TENANTSINTERFACE]) => {
      this.translate.setDefaultLang(companyInfo?.selectedLang?.LANG_CODE);
      this.translate.use(companyInfo?.selectedLang?.LANG_CODE);
      if (tenant?.selected_tenant?.id > 0) {
        this.localTenantId = tenant.selected_tenant.id;
      }
      this.getFreezeList();
    });
  }

  /**
   * Fetch client data if not provided through input
   */
  fetchClientDataIfNeeded(): void {
    const providedClient = this.client();
    if (providedClient?.clt_id) {
      // Client data is already provided
      this.localClient$.next(providedClient);
    } else if (this.clientId()) {
      // Fetch client data using clientId
      this.clientService.getClientById(this.clientId()).pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error fetching client data:', error);
          return of(null);
        })
      ).subscribe((client) => {
        if (client) {
          this.localClient$.next(client as Client);
        }
      });
    }
  }

  getFreezeList() {
    const clientId = this.clientId();
    if (!clientId) return;

    const subscriptionId = this.subscriptionId();
    const tenantId = this.tenantId() || this.localTenantId;

    const request$ = subscriptionId && tenantId
      ? this.freezeService.GetFreezeBySubscriptionId(tenantId, clientId, subscriptionId)
      : this.freezeService.GetFreezeByClientId(clientId);
    
    this.loading = true;
    request$.pipe(
      takeUntil(this.destroy$),
      catchError((error) => {
        this.toastMessageService.showError(error);
        return of([]);
      }),
      finalize(() => {
        this.loading = false;
        this.cd.detectChanges();
      })
    ).subscribe({
      next: (data: ClientSubscriptionFreezeResponse[]) => {
        const modifiedData = data.map((item) => {
          return {
            ...item,
            subscription_category: item.client_subscription.category,
            subscription_name: item.client_subscription.name_primary_language || item.client_subscription.name_secondary_language
          };
        });
        this.setFreezeData(modifiedData || []);
      }
    });
  }

  setFreezeData(freezes: ClientSubscriptionFreezeResponse[]) {
    this.freezeData$.next(freezes);
    this.selectAllFreezes = false;
    this.getFreezeTableColumns();
  }

  getFreezeTableColumns() {
    this.translate
      .get([
        'FREEZE.FREEZE_LIST.STARTDATE',
        'FREEZE.FREEZE_LIST.ENDDATE',
        'FREEZE.FREEZE_LIST.STATUS',
        'FREEZE.FREEZE_LIST.CREATEDBY',
        'FREEZE.FREEZE_LIST.CREATEDON',
        'FREEZE.FREEZE_LIST.UPDATEDBY',
        'FREEZE.FREEZE_LIST.UPDATEDON',
        'common.BUTTONS.BTN_ACTIONS',
        'FREEZE.FREEZE_LIST.SUBSCRIPTIONNAME',
        'FREEZE.FREEZE_LIST.SUBSCRIPTIONCATEGORY'
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.columnsFreezeTableTranslation(translations);
      });
  }

  /** Narrow layouts (e.g. subscription details) show created-by only; wider layouts show both. */
  private hasLimitedColumnSpace(): boolean {
    return true; //  return (this.subscriptionId() ?? 0) > 0 || !this.showHeader();
  }

  columnsFreezeTableTranslation(translations: any) {
    const columns = {
      STARTDATE: translations['FREEZE.FREEZE_LIST.STARTDATE'],
      ENDDATE: translations['FREEZE.FREEZE_LIST.ENDDATE'],
      STATUS: translations['FREEZE.FREEZE_LIST.STATUS'],
      CREATEDBY: translations['FREEZE.FREEZE_LIST.CREATEDBY'],
      CREATEDON: translations['FREEZE.FREEZE_LIST.CREATEDON'],
      UPDATEDBY: translations['FREEZE.FREEZE_LIST.UPDATEDBY'],
      UPDATEDON: translations['FREEZE.FREEZE_LIST.UPDATEDON'],
      ACTIONS: translations['common.BUTTONS.BTN_ACTIONS'],
      SUBSCRIPTIONNAME: translations['FREEZE.FREEZE_LIST.SUBSCRIPTIONNAME'],
      SUBSCRIPTIONCATEGORY: translations['FREEZE.FREEZE_LIST.SUBSCRIPTIONCATEGORY'],
    };

    const limitedSpace = this.hasLimitedColumnSpace();
    const showSubscriptionColumns = (this.subscriptionId() ?? 0) === 0;

    const tableColumns: any[] = [];

    if (showSubscriptionColumns) {
      tableColumns.push(
        {
          title: columns.SUBSCRIPTIONNAME,
          classes: 'py-3 text-600 w-12rem white-space-nowrap',
          sortable: true,
          filterable: true,
          selectable: false,
          dataProperty: 'subscription_name',
          type: 'default',
        },
        {
          title: columns.SUBSCRIPTIONCATEGORY,
          classes: 'py-3 text-600 w-12rem white-space-nowrap',
          sortable: true,
          filterable: true,
          selectable: false,
          dataProperty: 'subscription_category',
          type: 'default',
        }
      );
    }

    tableColumns.push(
      {
        title: columns.STARTDATE,
        classes: 'py-3 text-600 w-12rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'start_date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: { KeyName: 'start_date', format: 'dd MMM yyyy' },
        },
      },
      {
        title: columns.ENDDATE,
        classes: 'py-3 text-600 w-12rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'end_date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: { KeyName: 'end_date', format: 'dd MMM yyyy' },
        },
      },
      {
        title: columns.STATUS,
        classes: 'py-3 text-600 w-10rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'status',
        type: 'component',
        data: {
          name: FreezeStatusComponent,
          fetch_key: 'status',
        },
      },
      {
        title: columns.CREATEDBY,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'created_by',
        type: 'component',
        data: { name: StaffInfoComponent },
      },
      {
        title: columns.CREATEDON,
        classes: 'py-3 text-600 w-12rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'created_date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: { KeyName: 'created_date', format: 'dd MMM yyyy HH:mm' },
        },
      }
    );

    if (!limitedSpace) {
      tableColumns.push(
        {
          title: columns.UPDATEDBY,
          classes: 'py-3 white-space-nowrap',
          sortable: true,
          filterable: true,
          selectable: false,
          dataProperty: 'updated_by',
          type: 'component',
          data: { name: StaffInfoComponent },
        },
        {
          title: columns.UPDATEDON,
          classes: 'py-3 text-600 w-12rem white-space-nowrap',
          sortable: true,
          filterable: true,
          selectable: false,
          dataProperty: 'updated_date',
          type: 'component',
          data: {
            name: DateFormatedComponent,
            additionData: true,
            aData: { KeyName: 'updated_date', format: 'dd MMM yyyy HH:mm' },
          },
        }
      );
    }

    this.freezeTableColumns$.next(tableColumns);

    if (this.showTableActions()) {
      this.freezeTableColumns$.value.push({
        title: '',
        classes: 'py-2 white-space-nowrap w-8rem',
        dataProperty: 'actions',
        type: 'component',
        data: {
          name: ActionsComponent,
          additionData: true,
          aData: [
            'edit_freeze',
            'cancel_freeze',
            'stop_freeze'
          ],
        },
      });
    }
  }

  handleSelectAll(selection: any) {
    if (selection.checked) {
      this.showBulkActionBar = true;
    } else {
      this.showBulkActionBar = false;
    }
  }

  handleEvent(_event: any) {
    // Handle grid events if needed
  }

  onActionEvent(event: any) {
    switch (event.action) {
      case 'edit_freeze':
        {
          if (this.ShowEditOption(event.columnData)) {
            if (this.freezeV2Flow) {
              // Use new enhanced-freeze-sidebar for reschedule
              this.ShowRescheduleFreeze(event.columnData);
            } else {
              // Use old freeze-edit component
              this.ShowEditFreeze(event.columnData);
            }
          }
        }
        break;
      case 'cancel_freeze':
        {
          if (this.ShowCancelOption(event.columnData)) {
            this.ShowCancelFreeze(event.columnData);
          }
        }
        break;
      case 'stop_freeze':
        {
          if (this.ShowStopOption(event.columnData)) {
            this.ShowStopFreeze(event.columnData);
          }
        }
        break;
    }
  }

  // Old Flow - Freeze Edit Methods (using freeze-edit component)
  ShowEditFreeze(freeze: ClientSubscriptionFreezeResponse): void {
    this.selectedEditFreeze$.next(freeze);
    this.editFreeze$.next(true);
  }

  HideEditFreeze(isUpdated: boolean): void {
    this.selectedEditFreeze$.next(null);
    this.editFreeze$.next(false);
    if (isUpdated) {
      this.getFreezeList();
      this.isUpdated.emit(true);
    }
  }

  // New Flow (V2) - Freeze Reschedule Methods (using enhanced-freeze-sidebar)
  ShowRescheduleFreeze(freeze: ClientSubscriptionFreezeResponse): void {
    this.selectedRescheduleFreeze$.next(freeze);
    
    // Create a more complete subscription object for the reschedule sidebar
    const subscription = {
      id: freeze.client_subscription.id,
      client_id: freeze.client_id,
      company_id: freeze.company_id,
      tenant_id: freeze.tenant_id,
      name_primary_language: freeze.client_subscription.name_primary_language,
      name_secondary_language: freeze.client_subscription.name_secondary_language,
      category: freeze.client_subscription.category,
      status: freeze.client_subscription.status,
      subscription_category: freeze.client_subscription.category,
      freeze_can_only_billing_cycle: false,
      freeze_type: freeze.type, // Add freeze_type from the freeze object
      // Add other required properties with sensible defaults
      plan_id: 0,
      start_date: freeze.start_date,
      end_date: freeze.end_date,
    } as unknown as GetClientSubscriptionResponse;
    this.rescheduleSubscription$.next(subscription);
    
    // Fetch client data when opening sidebar (if not already available)
    // Open sidebar immediately, client data will load asynchronously
    this.fetchClientDataIfNeeded();
    this.rescheduleFreeze$.next(true);

  }

  HideRescheduleFreeze(isUpdated: boolean): void {
    this.selectedRescheduleFreeze$.next(null);
    this.rescheduleFreeze$.next(false);
    if (isUpdated) {
      this.getFreezeList();
      this.isUpdated.emit(true);
    }
  }

  // Freeze Cancel Methods
  ShowCancelFreeze(freeze: ClientSubscriptionFreezeResponse): void {
    if (this.freezeV2Flow) {
      // New V2 flow - Use enhanced sidebar
      this.selectedCancelFreezeV2$.next(freeze);
      const subscription = {
        id: freeze.client_subscription.id,
        client_id: freeze.client_id,
        company_id: freeze.company_id,
        tenant_id: freeze.tenant_id,
        name_primary_language: freeze.client_subscription.name_primary_language,
        name_secondary_language: freeze.client_subscription.name_secondary_language,
        category: freeze.client_subscription.category,
        status: freeze.client_subscription.status,
        subscription_category: freeze.client_subscription.category,
        freeze_can_only_billing_cycle: false,
        freeze_type: freeze.type,
        plan_id: 0,
        start_date: freeze.start_date,
        end_date: freeze.end_date,
      } as unknown as GetClientSubscriptionResponse;
      this.cancelSubscription$.next(subscription);
      this.cancelFreezeV2$.next(true);
      this.fetchClientDataIfNeeded();
    } else {
      // Old flow - Use freeze-cancel component
      this.selectedCancelFreeze$.next(freeze);
      this.cancelFreeze$.next(true);
    }
  }

  HideCancelFreeze(isUpdated: boolean): void {
    this.selectedCancelFreeze$.next(null);
    this.cancelFreeze$.next(false);
    this.selectedCancelFreezeV2$.next(null);
    this.cancelFreezeV2$.next(false);
    if (isUpdated) {
      this.getFreezeList();
      this.isUpdated.emit(true);
    }
  }

  // Freeze Stop Methods
  ShowStopFreeze(freeze: ClientSubscriptionFreezeResponse): void {
    if (this.freezeV2Flow) {
      // New V2 flow - Use enhanced sidebar
      this.selectedStopFreezeV2$.next(freeze);
      const subscription = {
        id: freeze.client_subscription.id,
        client_id: freeze.client_id,
        company_id: freeze.company_id,
        tenant_id: freeze.tenant_id,
        name_primary_language: freeze.client_subscription.name_primary_language,
        name_secondary_language: freeze.client_subscription.name_secondary_language,
        category: freeze.client_subscription.category,
        status: freeze.client_subscription.status,
        subscription_category: freeze.client_subscription.category,
        freeze_can_only_billing_cycle: false,
        freeze_type: freeze.type,
        plan_id: 0,
        start_date: freeze.start_date,
        end_date: freeze.end_date,
      } as unknown as GetClientSubscriptionResponse;
      this.stopSubscription$.next(subscription);
      this.stopFreezeV2$.next(true);
      this.fetchClientDataIfNeeded();
    } else {
      // Old flow - Use freeze-stop component
      this.selectedStopFreeze$.next(freeze);
      this.stopFreeze$.next(true);
    }
  }

  HideStopFreeze(isUpdated: boolean): void {
    this.selectedStopFreeze$.next(null);
    this.stopFreeze$.next(false);
    this.selectedStopFreezeV2$.next(null);
    this.stopFreezeV2$.next(false);
    if (isUpdated) {
      this.getFreezeList();
      this.isUpdated.emit(true);
    }
  }

  // Helper Methods
  ConvertToLocalTimezone(utcDate: Date | string) {
    return this.timeService.ConvertUTCToLocalDateTime(utcDate);
  }

  IsContract(freeze: ClientSubscriptionFreezeResponse): boolean {
    return freeze.client_subscription.category == PlanCategory.Contract || 
           freeze.client_subscription.category.toString() == PlanCategory[PlanCategory.Contract] ||
           freeze.client_subscription.category == PlanCategory.PayAsYouGo || 
           freeze.client_subscription.category.toString() == PlanCategory[PlanCategory.PayAsYouGo];
  }

  ShowEditOption(freeze: ClientSubscriptionFreezeResponse): boolean {
    return (freeze.status == ClientSubscriptionFreezeStatus.ScheduledFreeze || 
            freeze.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.ScheduledFreeze]) ||
           (freeze.status == ClientSubscriptionFreezeStatus.FreezeInprogress || 
            freeze.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.FreezeInprogress]);
  }

  ShowCancelOption(freeze: ClientSubscriptionFreezeResponse): boolean {
    return freeze.status == ClientSubscriptionFreezeStatus.ScheduledFreeze || 
           freeze.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.ScheduledFreeze];
  }

  ShowStopOption(freeze: ClientSubscriptionFreezeResponse): boolean {
    return freeze.status == ClientSubscriptionFreezeStatus.FreezeInprogress || 
           freeze.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.FreezeInprogress];
  }

  handleFreezeUpdateData(event:any)
  {
    if (event?.action && event.action == 'updated') this.isUpdated.emit(true);
  }

  get selectedEditFreeze(): any {
    return this.selectedEditFreeze$.value;
  }

  get selectedRescheduleFreeze(): any {
    return this.selectedRescheduleFreeze$.value;
  }

  get selectedCancelFreeze(): any {
    return this.selectedCancelFreeze$.value;
  }

  get selectedStopFreeze(): any {
    return this.selectedStopFreeze$.value;
  }

  get selectedCancelFreezeV2(): any {
    return this.selectedCancelFreezeV2$.value;
  }

  get selectedStopFreezeV2(): any {
    return this.selectedStopFreezeV2$.value;
  }
}
